import React from 'react'
import { SelectPicker } from 'rsuite'
import Icon from './Icon'

// redux
import { useSelector, useDispatch } from 'react-redux'
import { getContests, getSelectedContest } from '../redux/contests/selectors'
import { selectContest } from '../redux/contests/actions'
import { getDataContest } from '../redux/data/selectors'

// tools
import { formatDate } from '../tools/Time'
import { isShownRace } from '../tools/Races'
import { getContestDay } from '../tools/Weather'

/**
 * Get the label of a contest: its name and the day its laps were driven, which can differ from
 * the planned date
 * @param {Object} contest contest from the API
 * @returns {string} label
 */
const getContestLabel = ( contest ) =>
{
  const name = contest.description || `Race ${ contest.id }`
  return `${ name } · ${ formatDate( getContestDay( contest ) ) }`
}

/**
 * Pick the contest to show; the newest contest is followed live by default
 * @param {Object} props component props
 * @param {Function} props.onClose called after choosing, to close the preferences
 * @returns {JSX.Element} contest picker
 */
const ContestPicker = ( { onClose = null } ) =>
{
  // redux
  const dispatch = useDispatch()
  const contests = useSelector( getContests )
  const selected = useSelector( getSelectedContest )
  const loaded = useSelector( getDataContest )

  // the newest contest stands for following the newest contest
  const newest = contests.length ? contests[ 0 ].id : 0
  const value = selected || ( loaded ? loaded.id : newest )
  const data = contests.filter( isShownRace ).map( contest => ( { value: contest.id, label: getContestLabel( contest ) } ) )

  /**
   * Choose a contest; choosing the newest one follows new contests again
   * @param {number} id contest id
   * @returns {void}
   */
  const onSelect = ( id ) =>
  {
    dispatch( selectContest( id === newest ? 0 : id, newest ) )
    if ( onClose ) onClose()
  }

  /**
   * Render the chosen contest with the dropdown arrow used by all dropdowns
   * @param {string} label text to show
   * @returns {JSX.Element} toggle content
   */
  const renderToggle = ( label ) =>
  {
    return (
      <React.Fragment>
        <span className="label">{ label }</span>
        <Icon icon="arrow-down" className="carret"/>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <SelectPicker
        className="contest-picker"
        menuClassName="contest-picker-menu"
        data={ data }
        value={ value || null }
        onChange={ onSelect }
        cleanable={ false }
        searchable={ data.length > 10 }
        placeholder={ renderToggle( 'Loading races…' ) }
        renderValue={ ( id, item ) => renderToggle( item ? item.label : '' ) }
        placement="bottomStart"
        block
      />
      <p className="help">The newest race is shown and updated live. Choose another race to look back.</p>
    </React.Fragment>
  )
}

export default ContestPicker
